import React, { useEffect, useState } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import { ThemeContext, THEME_KEY, BRAND_COLOR_KEY } from "@/shared/lib/theme";
import type { ThemeContextShape } from "@/shared/lib/theme";
import { useSyncThirdPartyLocale } from "@/shared/hooks/useSyncThirdPartyLocale";

type ThemeType = "light" | "dark";

// One-time migration: the design system now has a fixed brand color, so any
// user-set custom color is no longer honored. Clear the legacy key once on
// first mount so localStorage stays clean.
function migrateLegacyBrandColor() {
  try {
    if (localStorage.getItem(BRAND_COLOR_KEY) !== null) {
      localStorage.removeItem(BRAND_COLOR_KEY);
      // eslint-disable-next-line no-console
      console.info(
        "[design-system] Cleared legacy BRAND_COLOR_KEY from localStorage",
      );
    }
  } catch {
    // ignore — localStorage may be unavailable in some embedded contexts
  }
}

export default function ThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  useEffect(() => {
    migrateLegacyBrandColor();
  }, []);

  const [theme, setTheme] = useState<ThemeType>(() => {
    try {
      const stored = localStorage.getItem(THEME_KEY);
      if (stored === "dark") return "dark";
      return "light";
    } catch {
      return "light";
    }
  });

  // Apply theme class to document root for the `.dark` selector in
  // variables.scss to take effect.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  const algorithm =
    theme === "dark" ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

  // Font stack shared with Tailwind / global SCSS — Open Sans first, native
  // OS fallback chain after.
  const fontFamily =
    '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  // AntD needs LITERAL color values for its tokens — it parses them through
  // its color algorithm to derive related tokens (elevated bgs, hover states,
  // borders on popups, etc). Passing "var(--…)" strings breaks that derivation
  // and AntD silently falls back to its dark-mode defaults, which is what
  // caused dropdowns / popovers to render with black surfaces in light mode.
  // Mirror the values from variables.scss so visual parity is preserved; the
  // CSS variables remain the source of truth for non-AntD consumers.
  const isDark = theme === "dark";
  const token = {
    colorPrimary: isDark ? "#3ECF92" : "#22b17a",
    colorText: isDark ? "#F4F4F5" : "#333333",
    colorTextSecondary: isDark ? "#A1A1AA" : "#6F6F6F",
    colorTextTertiary: isDark ? "#71717A" : "#9CA3AF",
    colorBgBase: isDark ? "#0A0A0A" : "#ffffff",
    colorBgContainer: isDark ? "#1F1F1F" : "#ffffff",
    colorBgElevated: isDark ? "#1F1F1F" : "#ffffff",
    colorBgLayout: isDark ? "#141414" : "#FAFAFA",
    // Reverted to pre-design-system border colors — the spec's #F5F5F5 /
    // #EBEBEB were too mild on white surfaces; AntD's borders disappeared.
    colorBorder: isDark ? "#525252" : "#e5e7eb",
    colorBorderSecondary: isDark ? "#707070" : "#d1d5db",
    borderRadius: 10,
    borderRadiusSM: 4,
    borderRadiusLG: 16,
    fontFamily,
  };

  // Form-control radius — every text/select/picker family pinned to 8px
  // across all three sizes (small/middle/large) so dense forms read as
  // restrained and clinical rather than the default AntD pillow corners
  // (large = 16px). Buttons/Cards/Dropdowns intentionally keep the broader
  // token-driven radii. Mirrors --radius-input from variables.scss.
  const INPUT_RADII = {
    borderRadius: 8,
    borderRadiusLG: 8,
    borderRadiusSM: 8,
  };

  // Form-control heights — pinned across the family so an inline row of
  // <Select> + <Input> + <DatePicker> + <Button> all render at the same
  // pixel height for whatever `size` prop is in use. AntD's defaults are
  // inconsistent across components (Button = 40 at middle, Select = 32
  // at middle), which produced visible mismatches whenever two form
  // controls sat side-by-side at the same `size` prop. Applying the
  // same height triplet to every form-control family fixes that.
  const INPUT_SIZES = {
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,
  };

  const components = {
    Button: { ...INPUT_SIZES },
    Input: { ...INPUT_SIZES, ...INPUT_RADII },
    InputNumber: { ...INPUT_SIZES, ...INPUT_RADII },
    Mentions: { ...INPUT_SIZES, ...INPUT_RADII },
    AutoComplete: { ...INPUT_SIZES, ...INPUT_RADII },
    Cascader: { ...INPUT_SIZES, ...INPUT_RADII },
    DatePicker: { ...INPUT_SIZES, ...INPUT_RADII },
    TimePicker: { ...INPUT_SIZES, ...INPUT_RADII },
    Card: { paddingLG: 24 },
    // Dropdown / Popover / Tooltip / Select popups share colorBgElevated, but
    // we set per-component overrides as well so any future AntD upgrade that
    // changes the derivation rule still lands on the right surface.
    Dropdown: {
      colorBgElevated: isDark ? "#1F1F1F" : "#ffffff",
      colorText: isDark ? "#F4F4F5" : "#333333",
    },
    Select: {
      colorBgElevated: isDark ? "#1F1F1F" : "#ffffff",
      colorText: isDark ? "#F4F4F5" : "#333333",
      ...INPUT_SIZES,
      ...INPUT_RADII,
    },
    Popover: {
      colorBgElevated: isDark ? "#1F1F1F" : "#ffffff",
      colorText: isDark ? "#F4F4F5" : "#333333",
    },
    Tooltip: {
      colorBgSpotlight: isDark ? "#1F1F1F" : "#333333",
      colorTextLightSolid: "#ffffff",
    },
  };

  const { antdLocale } = useSyncThirdPartyLocale();

  const value: ThemeContextShape = {
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        locale={antdLocale}
        theme={{
          algorithm,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          token: token as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          components: components as any,
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}
